import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
  Collapse,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import GeneralInformationView from "Components/Common/FireSafetyBuildingViewDetailComponent/GeneralInformationView";
import FloorWiseTenantView from "Components/Common/FireSafetyBuildingViewDetailComponent/FloorWiseTenantView";
import UtilityMechanicalSystemView from "Components/Common/FireSafetyBuildingViewDetailComponent/UtilityMechanicalSystemView";
import ElectricalSystemView from "Components/Common/FireSafetyBuildingViewDetailComponent/ElectricalSystemView";
import FireProtectionView from "Components/Common/FireSafetyBuildingViewDetailComponent/FireProtectionView";
import FireDetectorSystemView from "Components/Common/FireSafetyBuildingViewDetailComponent/FireDetectorSystemView";
import FireFightingPumpDetailView from "Components/Common/FireSafetyBuildingViewDetailComponent/FireFightingPumpDetailView";
import FloorWiseFireExtinguisherView from "Components/Common/FireSafetyBuildingViewDetailComponent/FloorWiseFireExtinguisherView";
import FloorWiseFireProtectionEquipmentView from "Components/Common/FireSafetyBuildingViewDetailComponent/FloorWiseFireProtectionEquipmentView";
import FireLifeSafetyView from "Components/Common/FireSafetyBuildingViewDetailComponent/FireLifeSafetyView";
import RefugeAreaDetailView from "Components/Common/FireSafetyBuildingViewDetailComponent/RefugeAreaDetailView";
import FireSafetyDocumentView from "Components/Common/FireSafetyBuildingViewDetailComponent/FireSafetyDocumentView";
import ResponsiblePersonAuthorityView from "Components/Common/FireSafetyBuildingViewDetailComponent/ResponsiblePersonAuthorityView";
import BuildingAuthorityView from "Components/Common/FireSafetyBuildingViewDetailComponent/BuildingAuthorityView";
import FireSafetyBuildingApprovalActionButton from "./FireSafetyBuildingApprovalActionButton";
import {
  allowedRolesForBuildingInformationComment,
  applicationStatusTypes,
} from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import CommentInput from "Components/Common/CommentInput";
import CommentBox from "Components/Common/CommentBox";
import AdminDiscussion from "common/AdminDiscussion";
import { FIRE_SAFETY_CONSUMER } from "common/constant";
import AutomaticSprinklerSystemView from "Components/Common/FireSafetyBuildingViewDetailComponent/AutomaticSprinklerSystemView";
import { ApplicationApprovalTimelineChart } from "common/ApplicationApprovalTimelineChart";
import FireDetectionTypeView from "Components/Common/FireSafetyBuildingViewDetailComponent/FireDetectionTypeView";

import {
  useLazyGetApplicationCommentQuery,
  usePostApplicationCommentMutation,
} from "apis/ApplicationRequestComment";
import { useGetFireSafetyApplicationDetailQuery } from "apis/FireSafety/FireApplicationDetail";

import { isAuthorizedUser, isCommentInputVisible } from "utils";

import { DialogHeader } from "./style";
import { useFloorQuery } from "apis/Floor";

const FireSafetyBuildingViewModal = ({
  handleClose,
  applicationId,
  isBuildingObservationSideBarSelected,
  t,
}) => {
  const {
    data: { data: applicationData = {} } = {},
    isLoading: isConsumerDataLoading,
    isFetching,
    isError,
  } = useGetFireSafetyApplicationDetailQuery(applicationId);

  const [getAdminComments, { data: { data: commentData } = {} }] =
    useLazyGetApplicationCommentQuery();

  const { data: { data: floors } = {}, isLoading: isFloorsLoading } =
    useFloorQuery({}, true);

  const [addComment] = usePostApplicationCommentMutation();

  const { roles } = useSelector((state) => state.Login);

  const [openDiscussion, setOpenDiscussion] = useState(true);

  const isAdmin = useMemo(() => !roles.includes(FIRE_SAFETY_CONSUMER), [roles]);

  const handleDiscussionCollapse = () => {
    setOpenDiscussion(!openDiscussion);
  };

  const addNewComment = async (comment) => {
    await addComment(
      {
        buildingInformationId: applicationId,
        comment,
      },
      {},
      true
    );
  };

  useEffect(() => {
    if (commentData?.length >= 5) setOpenDiscussion((prev) => !prev);
  }, [commentData]);

  useEffect(() => {
    if (
      applicationId &&
      isAuthorizedUser(roles, [
        ...allowedRolesForBuildingInformationComment,
        FIRE_SAFETY_CONSUMER,
      ])
    ) {
      (async () => {
        try {
          await getAdminComments(applicationId, {}, true);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [applicationId, roles]);

  useEffect(() => {
    if (isError) {
      handleClose();
    }
  }, [isError]);

  if (isConsumerDataLoading || isFetching || isFloorsLoading) {
    return <Backdrop open />;
  }

  return (
    <Dialog
      open={!!applicationId}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      <DialogHeader>
        {t("Register Building Details")}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <DialogContent>
        <DialogContentText variant="subtitle2" component="div">
          <div className="mt-2 ">
            <Row className="d-flex  mb-2">
              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Inspection Building Name")}:</Col>
                  <Col
                    md={7}
                    className="fw-semibold"
                    style={{ wordBreak: "break-word" }}
                  >
                    {applicationData?.developerBuilding?.name ?? "--"}
                  </Col>
                </Row>
              </Col>
            </Row>
            {!!applicationData && (
              <>
                {!!applicationData && (
                  <BuildingAuthorityView
                    buildingAuthorities={[
                      applicationData?.authority,
                      applicationData?.buildingAuthority,
                    ]}
                  />
                )}
                {!!applicationData?.responsiblePersonAuthorities &&
                  !!applicationData.responsiblePersonAuthorities.length && (
                    <ResponsiblePersonAuthorityView
                      responsiblePersonAuthorities={
                        applicationData.responsiblePersonAuthorities
                      }
                    />
                  )}

                {!!applicationData && (
                  <GeneralInformationView applicationData={applicationData} />
                )}

                {!!applicationData?.floorWiseTenants &&
                  !!applicationData.floorWiseTenants.length && (
                    <FloorWiseTenantView
                      floorWiseTenants={applicationData.floorWiseTenants}
                      floors={applicationData?.fireSafetyAddress?.floors ?? []}
                    />
                  )}
                {applicationData?.utilityMechanicalSystem && (
                  <UtilityMechanicalSystemView
                    utilityMechanicalSystem={
                      applicationData.utilityMechanicalSystem
                    }
                  />
                )}
                {!!applicationData?.utilityMechanicalSystem
                  ?.electricalSystem && (
                  <ElectricalSystemView
                    electricalSystem={
                      applicationData.utilityMechanicalSystem.electricalSystem
                    }
                  />
                )}
                {!!applicationData?.fireProtection && (
                  <>
                    <FireProtectionView
                      fireProtection={applicationData.fireProtection}
                    />
                    {!!applicationData.fireProtection.basementSprinklers
                      ?.length && (
                      <AutomaticSprinklerSystemView
                        sprinklers={
                          applicationData.fireProtection.basementSprinklers
                        }
                        floorOptions={floors}
                        isBasement
                      />
                    )}
                    {!!applicationData.fireProtection.floorSprinklers
                      ?.length && (
                      <AutomaticSprinklerSystemView
                        sprinklers={
                          applicationData.fireProtection.floorSprinklers
                        }
                        floorOptions={floors}
                      />
                    )}

                    <FireDetectorSystemView
                      fireProtection={applicationData.fireProtection}
                    />
                    {!!applicationData.fireProtection.detectorSystems
                      ?.length && (
                      <FireDetectionTypeView
                        detectorSystems={
                          applicationData.fireProtection.detectorSystems
                        }
                        floorOptions={floors}
                      />
                    )}
                  </>
                )}
                {!!applicationData?.fireProtection?.fireFightingPumps &&
                  !!applicationData.fireProtection.fireFightingPumps.length && (
                    <FireFightingPumpDetailView
                      fireFightingPumps={
                        applicationData.fireProtection.fireFightingPumps
                      }
                    />
                  )}
                {!!applicationData?.fireProtection
                  ?.floorWiseFireExtinguishers &&
                  !!applicationData.fireProtection.floorWiseFireExtinguishers
                    .length && (
                    <FloorWiseFireExtinguisherView
                      floorWiseFireExtinguishers={
                        applicationData.fireProtection
                          .floorWiseFireExtinguishers
                      }
                    />
                  )}
                {!!applicationData?.floorWiseFireProtectionEquipments &&
                  !!applicationData.floorWiseFireProtectionEquipments
                    .length && (
                    <FloorWiseFireProtectionEquipmentView
                      floorWiseFireProtectionEquipments={
                        applicationData.floorWiseFireProtectionEquipments
                      }
                    />
                  )}
                {!!applicationData?.lifeSafety && (
                  <FireLifeSafetyView lifeSafety={applicationData.lifeSafety} />
                )}
                {!!applicationData?.lifeSafety?.refugeAreas &&
                  !!applicationData.lifeSafety.refugeAreas.length && (
                    <RefugeAreaDetailView
                      refugeAreas={applicationData.lifeSafety.refugeAreas}
                    />
                  )}
                {!!applicationData?.lifeSafety?.dbrDocuments &&
                  !!applicationData.lifeSafety.dbrDocuments.length && (
                    <FireSafetyDocumentView
                      causeAndEffectMatrixDocumentUrl={
                        applicationData?.fireProtection
                          ?.causeAndEffectMatrixDocumentUrl ?? ""
                      }
                      dbrDocuments={applicationData.lifeSafety.dbrDocuments}
                    />
                  )}

                {applicationData?.buildingInformationStatus?.name.toLowerCase() !==
                  "saved" && (
                  <>
                    <div
                      className="fs-4 fw-semibold mb-3 border-bottom"
                      onClick={handleDiscussionCollapse}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <div>{t("Discussion")}</div>{" "}
                        <p>
                          {openDiscussion ? <ExpandLess /> : <ExpandMore />}
                        </p>
                      </Stack>
                    </div>
                    <Collapse in={openDiscussion} timeout="auto" unmountOnExit>
                      {!!applicationData?.buildingInformationStatus?.name &&
                        isCommentInputVisible(
                          applicationData.buildingInformationStatus.name,
                          roles,
                          !!commentData?.length,
                          true
                        ) && (
                          <CommentInput
                            handleSubmit={addNewComment}
                            applicationId={applicationData?.id ?? ""}
                            isFireSafety
                          />
                        )}
                      {commentData?.length ? (
                        commentData.map(
                          ({ comment, createdAt, name }, index) => (
                            <CommentBox
                              comment={comment}
                              name={name}
                              createdAt={createdAt}
                              key={index}
                            />
                          )
                        )
                      ) : (
                        <Card variant="outlined" className="mt-2 p-0">
                          <CardContent className="pt-1">
                            <Typography color="GrayText" gutterBottom>
                              {t("No comment")}
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                    </Collapse>

                    {isAdmin && (
                      <AdminDiscussion
                        buildingInformationId={applicationId}
                        isFireSafety
                      />
                    )}
                    {isAdmin && !!applicationId && (
                      <ApplicationApprovalTimelineChart
                        applicationId={applicationId}
                        applicationStatus={
                          applicationData.buildingInformationStatus?.name
                        }
                        isFireSafety
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </DialogContentText>
      </DialogContent>
      {applicationData &&
        applicationData?.buildingInformationStatus?.id !==
          applicationStatusTypes["Saved"] &&
        !isBuildingObservationSideBarSelected && (
          <FireSafetyBuildingApprovalActionButton
            isBuildingNameSelected={!!applicationData?.developerBuilding?.name}
            applicationData={applicationData}
            handleClose={handleClose}
          />
        )}
    </Dialog>
  );
};

FireSafetyBuildingViewModal.propTypes = {
  handleClose: PropTypes.func,
  applicationId: PropTypes.string,
  isBuildingObservationSideBarSelected: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyBuildingViewModal);
